/**
 * Behavior hook for the OR-6126 concept-group preview review page.
 *
 * Owns the preview-detail load + the name/create flow + latest-effective
 * resolution, so the page component stays a thin presentational renderer and the
 * behavior is unit-testable in isolation (per the container/component +
 * one-hook-per-concern architecture). Returns ``nameErrorId`` as a message id (or
 * null) rather than a formatted string so the hook is intl-free and testable; the
 * component formats it.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { getProjectConceptGroupPath } from '@verantos-dev/core-constants';
import { conceptGroupKeys } from '@verantos-dev/feature-concept-group-detail';
import {
  AuthFetchError,
  useConceptGroupNameAvailability,
  useConceptGroupPreview,
  useConfirmPreview,
  useValidatePreviewName,
  type ConceptGroupPreviewDetail,
} from '@verantos-dev/feature-epi-chat';

import { useActiveChatContext } from '../../../context/ActiveChatContext';
import { useChatPreservingNavigate } from '../../../hooks/useChatPreservingNavigate';
import { projectPageKeys } from '../../../lib/api/queryKeys';

const MSG = {
  nameRequired: 'epiChat.conceptGroupPreview.create.nameRequired',
  duplicateName: 'epiChat.conceptGroupPreview.create.duplicateName',
  error: 'epiChat.conceptGroupPreview.create.error',
  // OR-6288 Stage C CAS: the draft moved after this view loaded (409 on confirm).
  conflict: 'epiChat.conceptGroupPreview.create.conflict',
} as const;

// Verdicts about the name itself, which a rename retires. A conflict or a
// transport error survives one: neither was a judgement on the name.
const NAME_SCOPED_ERRORS: readonly string[] = [MSG.nameRequired, MSG.duplicateName];

export interface ConceptGroupPreviewPageModel {
  detail: ConceptGroupPreviewDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Editable concept-group name, seeded from the proposal once detail loads. */
  name: string;
  setName: (value: string) => void;
  /** Message id for the inline name error, or ``null``. */
  nameErrorId: string | null;
  /** The name already belongs to a published group — Approve is blocked. */
  isNameTaken: boolean;
  /** The availability verdict is in flight — Approve waits rather than racing it. */
  isCheckingName: boolean;
  /** True once the proposal has been created at least once (resolve-to-effective). */
  isConfirmed: boolean;
  /** The latest created org-library group id, when confirmed. */
  createdGroupId: string | null;
  /** A create / name-validation request is in flight. */
  creating: boolean;
  /** Validate the name, confirm the preview, then refetch so the page flips in place. */
  handleCreate: () => Promise<void>;
  /** Name of the group just created this session — drives the success toast. */
  createdName: string | null;
  dismissCreatedToast: () => void;
}

export const useConceptGroupPreviewPage = ({
  previewId,
  projectId,
}: {
  previewId: string;
  projectId: string;
}): ConceptGroupPreviewPageModel => {
  const [name, setNameState] = useState('');
  const [nameErrorId, setNameErrorId] = useState<string | null>(null);
  const [createdName, setCreatedName] = useState<string | null>(null);
  // Optimistic create latch: stays true from the click through the redirect so
  // the button can't re-fire in the window after the confirm mutation settles
  // but before the page navigates away. Reset only on a failed attempt.
  const [submitting, setSubmitting] = useState(false);

  const navigate = useChatPreservingNavigate();
  const queryClient = useQueryClient();
  const detailQuery = useConceptGroupPreview(previewId || null);
  const validateName = useValidatePreviewName();
  const confirmPreview = useConfirmPreview();
  // TODO: cleanup with concept_group_rename ff
  const precheckEnabled = useFeatureFlagSplit('concept_group_rename', false) as boolean;
  // OR-6247 A2: the chat panel sharing this project page (via ActiveChatProvider).
  // ``null`` when there's no chat — e.g. a deep-link straight to a confirmed
  // preview — in which case the optimistic flip is simply skipped.
  const activeChat = useActiveChatContext();

  const detail = detailQuery.data;
  // Renaming commits through PATCH and lands here as a new committed name, never
  // through ``setName``. Retire a name verdict with the name it judged, or it
  // outranks the pre-check for the new name until the page unmounts.
  useEffect(() => {
    if (!detail?.conceptGroupName) return;
    setNameState(detail.conceptGroupName);
    setNameErrorId((prev) => (prev && NAME_SCOPED_ERRORS.includes(prev) ? null : prev));
  }, [detail?.conceptGroupName]);

  const createdGroupId = detail?.createdConceptGroupId ?? null;
  // Gate confirmation on the actual lifecycle status ONLY — NOT on the presence
  // of ``createdConceptGroupId``. Since OR-6289 (stable-id) the real catalog id is
  // minted at proposal *save*, so ``createdConceptGroupId`` is populated from the
  // moment of creation, well before the researcher approves. Keying ``isConfirmed``
  // (and the redirect below) on it made the page treat a brand-new Draft as already
  // confirmed → it redirected straight to the read-only canonical page and the
  // Approve/Create button never appeared. ``status`` is reliably ``"pending"`` until
  // confirm and ``"confirmed"`` after, so it's the correct gate.
  const isConfirmed = detail?.status === 'confirmed';
  const creating = submitting || confirmPreview.isPending || validateName.isPending;

  // Once the proposal is CONFIRMED (fresh confirm OR a deep-link to an
  // already-confirmed preview), redirect to the REAL concept-group route so the
  // researcher lands on the canonical page — with its breadcrumb — instead of
  // the preview URL resolving the detail in place. ``replace`` so Back doesn't
  // return to the now-dead preview. ``?chat=`` is preserved by the navigate hook.
  useEffect(() => {
    if (isConfirmed && createdGroupId && projectId) {
      navigate(getProjectConceptGroupPath(projectId, createdGroupId), { replace: true });
    }
  }, [isConfirmed, createdGroupId, projectId, navigate]);

  // Surface a collision on open rather than on the Approve click. Derived, not stored:
  // ``nameErrorId`` is cleared by ``setName``, which an effect would fight.
  // Drafts only: a confirmed proposal is already published under its name, so
  // asking whether that name is free would always answer no. Keyed off
  // ``isConfirmed`` — the same predicate the redirect uses — rather than a second
  // status test that a new status value could silently turn the check off.
  const isDraft = Boolean(detail) && !isConfirmed;
  const nameAvailability = useConceptGroupNameAvailability({
    name,
    projectId,
    enabled: precheckEnabled && isDraft,
  });
  const isNameTaken = precheckEnabled && nameAvailability.data === false;
  // Approve waits for the verdict rather than racing it — clicking before it
  // lands is what made a collision look like it only surfaced on the click.
  //
  // Outstanding, not merely in flight: before the detail resolves there is no
  // name to ask about, so the request has not started and ``isFetching`` is
  // still false. Keying on that flashed a live button for a frame before the
  // skeleton replaced it. Resolved the moment the answer cannot change —
  // a verdict arrived, the check failed (fails open, since confirm re-validates
  // server-side), or the detail landed with no name, which disables Approve on
  // its own and means the query will never run.
  const isCheckingName =
    precheckEnabled &&
    !isConfirmed &&
    !nameAvailability.isError &&
    nameAvailability.data === undefined &&
    (!detail || Boolean(name.trim()));

  // Editing the name clears a stale error so the helper text doesn't linger.
  const setName = useCallback((value: string) => {
    setNameState(value);
    setNameErrorId((prev) => (prev ? null : prev));
  }, []);

  const handleCreate = useCallback(async () => {
    const trimmed = name.trim();
    if (!previewId || !trimmed) {
      setNameErrorId(MSG.nameRequired);
      return;
    }
    setNameErrorId(null);
    // Latch immediately so the button is blocked for the whole flow, including
    // the refetch window after the confirm mutation settles. Reset only on a
    // failed attempt; on success the redirect effect unmounts this page.
    setSubmitting(true);
    // OR-6247 A2: flip the chat to its working state the instant Create fires.
    // Creating a group resumes the chat server-side (a REST turn, no socket
    // user_message), so without this the chat sits idle until the resume's
    // RUN_STARTED round-trips. Cleared on any path that does NOT proceed to the
    // resume (duplicate name / error); on success the real RUN_STARTED
    // supersedes the placeholder, and the A3 watchdog backstops a resume that
    // never kicks. No-op when there's no active chat panel.
    activeChat?.page.beginResume();
    try {
      const isValid = await validateName.mutateAsync({ conceptGroupName: trimmed, projectId });
      if (!isValid) {
        setNameErrorId(MSG.duplicateName);
        setSubmitting(false);
        activeChat?.page.endResume();
        return;
      }
      await confirmPreview.mutateAsync({
        previewId,
        conceptGroupName: trimmed,
        projectId,
        // OR-6288 Stage C CAS: publish the exact revision this view reviewed. A
        // draft that moved (edited in chat) since is refused with HTTP 409 — the
        // researcher re-reviews rather than silently publishing a stale definition.
        // Sourced exactly like ConceptGroupDetailView.handleApprove. Legacy previews
        // without a rev pass ``undefined`` (no CAS check).
        expectedRev: detail?.draftRev ?? undefined,
      });
      setCreatedName(trimmed);
      // The canonical ``/concept-group/:id`` page renders THIS review while its
      // own detail says Draft. When save happens on that mount (the default
      // since the create modal forwards there), the redirect below navigates to
      // the URL we're already on — a no-op — so the stale Draft detail must be
      // invalidated here or the page spins on "redirecting" forever.
      queryClient.invalidateQueries({ queryKey: conceptGroupKeys.all });
      // The list's Draft chip flips to Ready without a manual refresh.
      queryClient.invalidateQueries({ queryKey: projectPageKeys.artifactsForProject(projectId) });
      // Refetch so ``createdConceptGroupId`` lands; the redirect effect then
      // routes to the real concept-group page. The agent continues server-side.
      await detailQuery.refetch();
    } catch (error) {
      // Mirror ConceptGroupDetailView.handleApprove: a 409 is the recoverable CAS
      // conflict — surface the "refresh and review again" message and refetch the
      // draft detail so the researcher re-reviews the CURRENT revision. Classify on
      // the structured ``.status`` (the codebase idiom) rather than string-matching
      // the message, so a reworded error can't silently misroute the conflict.
      const conflict = error instanceof AuthFetchError && error.status === 409;
      setNameErrorId(conflict ? MSG.conflict : MSG.error);
      setSubmitting(false);
      activeChat?.page.endResume();
      if (conflict) await detailQuery.refetch();
    }
  }, [
    name,
    previewId,
    projectId,
    validateName,
    confirmPreview,
    detailQuery,
    activeChat,
    detail?.draftRev,
    queryClient,
  ]);

  return {
    detail,
    isLoading: detailQuery.isLoading,
    isError: detailQuery.isError,
    name,
    setName,
    // A failed attempt outranks the pre-check — it reports what actually happened.
    nameErrorId: nameErrorId ?? (isNameTaken ? MSG.duplicateName : null),
    isNameTaken,
    isCheckingName,
    isConfirmed,
    createdGroupId,
    creating,
    handleCreate,
    createdName,
    dismissCreatedToast: useCallback(() => setCreatedName(null), []),
  };
};
